/**
 * Combines items and recommendations into a single list of pairs for display.
 * 
 * @param {Array} items - List of item objects, each with at least an 'id' property.
 * @param {Array} recommendations - List of recommendation objects, each with at least an 'item' property containing an item object and a 'type' property.
 * @param {boolean} includeSwaps - If true, includes recommendations of type 'swap' and 'compliance'.
 * @param {boolean} excludeRemovals - If true, excludes recommendations that are accepted or custom removals.
 * @returns {Array} Combined list of objects, each with 'item' and 'rec' properties.
 */
export function combineItemsAndRecs(items, recommendations, includeSwaps = false, excludeRemovals = false) {
    // Build a map of items by id for quick lookup
    const itemsById = Object.fromEntries(items.map(item => [item.id, item]));

    // Collect all item ids: from items and from recommendations (for add recs)
    const recItemIds = recommendations?.map(r => r.item.id);
    const allItemIds = Array.from(new Set([...items.map(i => i.id), ...recItemIds]));

    // Build the display list
    let itemsWithRecs = allItemIds.map((id) => {
        const item = itemsById[id] || {};
        let rec = recommendations.find(r => r.item.id === id);
        // Remove rec if it's type swap or compliance
        if (rec && !includeSwaps && (rec.type === 'swap' || rec.type === 'compliance')) {
            rec = undefined;
        }
        // Remove rec and item if it's an accepted removal
        if (rec && excludeRemovals && (rec.status === 'custom_removal' || (rec.status === 'accepted' && rec.type === 'removal'))) {
            return { item: null, rec: null };
        }
        return { item, rec };
    }).filter(entry => entry.item || entry.rec).sort((a, b) => (b.rec ? 1 : 0) - (a.rec ? 1 : 0));

    return itemsWithRecs;
}

export default combineItemsAndRecs;